/**
 * D-3 导演台入口闸门（用户裁定：可点击 → 弹窗阻止，**不得置灰**）
 *
 * 为什么不置灰：置灰是"系统知道但你不知道为什么"，用户只会认为产品坏了。
 * 保留可点击并把原因讲清楚，才是对专业创作者的尊重。
 *
 * 本模块是纯函数：输入能力报告，输出对话框规格。不碰 DOM，便于单测与换皮。
 */

import type { CapabilityReport } from './capability';
import type { Guidance } from './browserGuide';

export type GateAction =
  | 'enter' // 直接进入
  | 'block' // 弹窗阻止，不提供继续
  | 'blockWithForce'; // 弹窗阻止，但允许"仍以流畅档进入"

export interface GateDecision {
  action: GateAction;
  /** 入口是否渲染为可点击。三种情形全为 true —— 本设计明确不使用置灰 */
  entryClickable: true;
  /** 阻止时展示的对话框内容；可进入时为 null */
  dialog: GateDialog | null;
  /** 强制进入后锁定的画质档位（仅 blockWithForce 有意义） */
  forcedTier: CapabilityReport['tier'] | null;
}

export interface GateDialog {
  title: string;
  /** 影响范围声明，必须出现：防止用户以为整个软件坏了 */
  scope: string;
  /** 可执行步骤，来自 buildGuidance() */
  steps: Guidance['steps'];
  /** 兜底建议 */
  fallback: string;
  /** 主按钮文案 */
  primaryLabel: string;
  /** 是否显示"仍要进入"次按钮 */
  showForceEntry: boolean;
  /** 次按钮文案（showForceEntry 为 true 时必填） */
  forceLabel: string | null;
}

/**
 * 支撑"必备三件事"第 3 条：一键复制探测报告，便于我们回收真实环境数据。
 * 只输出非敏感字段，不含任何 Cookie / token / 路径信息。
 */
export function formatCapabilityReport(report: CapabilityReport, engine: string): string {
  return [
    `内核：${engine}`,
    `WebGL2：${report.webgl2 ? '可用' : '不可用'}`,
    `GPU：${report.gpuRenderer ?? '未上报'}`,
    `软件渲染：${report.softwareRenderer ? '是' : '否'}`,
    `DPR：${report.devicePixelRatio}`,
    `内存：${report.deviceMemoryGiB === null ? '未公开' : `${report.deviceMemoryGiB} GiB`}`,
    `CPU 逻辑核：${report.hardwareConcurrency === null ? '未公开' : report.hardwareConcurrency}`,
    `判定档位：${report.tier}`,
  ].join('\n');
}

export function decideGate(report: CapabilityReport, guidance: Guidance): GateDecision {
  if (report.directorAvailable) {
    return { action: 'enter', entryClickable: true, dialog: null, forcedTier: null };
  }

  // 软件渲染是唯一"能跑但体验差"的情形 —— 只有它值得给用户自担风险的选项。
  // 判据用事实而非文案，避免改了提示措辞就把放行条件改没了。
  const forceable = report.webgl2 && report.softwareRenderer;

  return {
    action: forceable ? 'blockWithForce' : 'block',
    entryClickable: true,
    forcedTier: forceable ? 'low' : null,
    dialog: {
      title: guidance.headline,
      scope: guidance.scope,
      steps: guidance.steps,
      fallback: guidance.fallback,
      primaryLabel: forceable ? '我知道了' : '去设置浏览器',
      showForceEntry: forceable,
      forceLabel: forceable ? '仍以流畅档进入（画面会较粗糙）' : null,
    },
  };
}
